// components/UserStats.js
import React, { useState, useEffect } from 'react';
import { getCurrentUserId } from '../services/database';
import proxyapi from '../services/ProxyApi';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Calendar, Activity, Clock } from 'lucide-react';

const UserStats = () => {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalViewsGenerated: 0,
    totalRevisionsCreated: 0
  });
  const [dailyStats, setDailyStats] = useState([]);
  const [monthlyStats, setMonthlyStats] = useState([]);
  const [view, setView] = useState('total'); // 'total', 'daily', or 'monthly'
  const [dateRange, setDateRange] = useState({
    startDate: getDefaultStartDate(),
    endDate: getDefaultEndDate()
  });
  const [error, setError] = useState(null);

  // Helper functions for default dates
  function getDefaultStartDate() {
    const date = new Date();
    date.setDate(date.getDate() - 30); // Default to last 30 days
    return date.toISOString().split('T')[0]; // Format as YYYY-MM-DD
  }

  function getDefaultEndDate() {
    return new Date().toISOString().split('T')[0]; // Today
  }

  useEffect(() => {
    const fetchStats = async () => {
      try {
        setLoading(true);
        const userId = await getCurrentUserId();
        
        if (!userId) {
          setError('User not authenticated');
          setLoading(false);
          return;
        }

        // Fetch total stats
        const totalResponse = await proxyapi.get(`/api/v1/users/${userId}/stats?format=total&start_date=${dateRange.startDate}&end_date=${dateRange.endDate}`);
        setStats(totalResponse.data.data);

        // Fetch daily stats
        const dailyResponse = await proxyapi.get(`/api/v1/users/${userId}/stats?format=daily&start_date=${dateRange.startDate}&end_date=${dateRange.endDate}`);
        setDailyStats(dailyResponse.data.data.dailyStats || []);

        // Fetch monthly stats
        const monthlyResponse = await proxyapi.get(`/api/v1/users/${userId}/stats?format=monthly&start_date=${dateRange.startDate}&end_date=${dateRange.endDate}`);
        setMonthlyStats(monthlyResponse.data.data.monthlyStats || []);

        setError(null);
      } catch (err) {
        console.error('Error fetching user stats:', err);
        setError('Failed to load usage statistics. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, [dateRange]);

  const handleDateRangeChange = (e) => {
    const { name, value } = e.target;
    setDateRange(prev => ({ ...prev, [name]: value }));
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const formatMonth = (monthStr) => {
    if (!monthStr) return '';
    const [year, month] = monthStr.split('-');
    return new Date(year, month - 1).toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  };

  if (loading) {
    return (
      <div className="bg-white shadow rounded-lg p-6 mb-6 flex justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  return (
    <div className="bg-white shadow rounded-lg p-6 mb-6">
      <h2 className="text-xl font-semibold mb-4 flex items-center">
        <Activity className="mr-2" size={20} />
        Usage Statistics
      </h2>

      {error ? (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      ) : (
        <>
          {/* Date Range Selector */}
          <div className="flex flex-wrap items-center mb-6 gap-4">
            <div className="flex items-center">
              <Calendar className="mr-2 text-gray-600" size={16} />
              <span className="text-gray-600 mr-2">Date Range:</span>
            </div>
            <div className="flex flex-wrap gap-2">
              <input
                type="date"
                name="startDate"
                value={dateRange.startDate}
                onChange={handleDateRangeChange}
                className="border rounded p-1 text-sm"
              />
              <span className="text-gray-600">to</span>
              <input
                type="date"
                name="endDate"
                value={dateRange.endDate}
                onChange={handleDateRangeChange}
                className="border rounded p-1 text-sm"
              />
            </div>
          </div>

          {/* View Selector */}
          <div className="flex mb-6 border-b">
            <button
              onClick={() => setView('total')}
              className={`pb-2 px-4 ${view === 'total' ? 'border-b-2 border-black font-medium' : 'text-gray-500'}`}
            >
              Total
            </button>
            <button
              onClick={() => setView('daily')}
              className={`pb-2 px-4 ${view === 'daily' ? 'border-b-2 border-black font-medium' : 'text-gray-500'}`}
            >
              Daily
            </button>
            <button
              onClick={() => setView('monthly')}
              className={`pb-2 px-4 ${view === 'monthly' ? 'border-b-2 border-black font-medium' : 'text-gray-500'}`}
            >
              Monthly
            </button>
          </div>

          {/* Statistics Display */}
          {view === 'total' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="text-lg font-medium text-gray-700 mb-2">Total Views Generated</div>
                <div className="text-3xl font-bold">{stats.totalViewsGenerated}</div>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="text-lg font-medium text-gray-700 mb-2">Total Revisions Created</div>
                <div className="text-3xl font-bold">{stats.totalRevisionsCreated}</div>
              </div>
            </div>
          )}

          {view === 'daily' && (
            <div>
              <h3 className="text-lg font-medium mb-4">Daily Usage</h3>
              {dailyStats.length === 0 ? (
                <p className="text-gray-500">No daily statistics available for the selected date range.</p>
              ) : (
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={dailyStats}
                      margin={{ top: 20, right: 30, left: 20, bottom: 30 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis 
                        dataKey="date" 
                        tickFormatter={formatDate} 
                        angle={-45}
                        textAnchor="end"
                        height={70}
                      />
                      <YAxis />
                      <Tooltip 
                        labelFormatter={date => `Date: ${new Date(date).toLocaleDateString()}`}
                        formatter={(value, name) => {
                          // More robust approach to handle the dataKey
                          if (name === 'viewsGenerated') return [value, 'Views'];
                          if (name === 'revisionsCreated') return [value, 'Revisions'];
                          return [value, name]; // Fallback
                        }}
                      />
                      <Legend />
                      <Bar dataKey="viewsGenerated" name="Views" fill="#3B82F6" />
                      <Bar dataKey="revisionsCreated" name="Revisions" fill="#10B981" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>
          )}

          {view === 'monthly' && (
            <div>
              <h3 className="text-lg font-medium mb-4">Monthly Usage</h3>
              {monthlyStats.length === 0 ? (
                <p className="text-gray-500">No monthly statistics available for the selected date range.</p>
              ) : (
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={monthlyStats}
                      margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis 
                        dataKey="month" 
                        tickFormatter={formatMonth}
                      />
                      <YAxis />
                      <Tooltip 
                        labelFormatter={month => `Month: ${formatMonth(month)}`}
                        formatter={(value, name) => {
                          // More robust approach to handle the dataKey
                          if (name === 'viewsGenerated') return [value, 'Views'];
                          if (name === 'revisionsCreated') return [value, 'Revisions'];
                          return [value, name]; // Fallback
                        }}
                      />
                      <Legend />
                      <Bar dataKey="viewsGenerated" name="Views" fill="#3B82F6" />
                      <Bar dataKey="revisionsCreated" name="Revisions" fill="#10B981" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>
          )}

          <div className="mt-4 text-xs text-gray-500">
            <p><Clock className="inline mr-1" size={12} /> Statistics updated as of {new Date().toLocaleString()}</p>
          </div>
        </>
      )}
    </div>
  );
};

export default UserStats;